import { getApiUrl, getApiHeaders } from './apiConfig';
import { getAnonymousUserId } from './anonymousUser';
import { getFirstUtmFromStorage, getMetaMatchParams } from './analytics';
import { resumePendingSignupComplete, trackSignupCompleteOnce } from './eventTracker';

const createBrowserEventId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `registration-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
};

/**
 * 사용자 초기화와 CompleteRegistration CAPI에 공통으로 전달할 귀속 payload.
 * 무료 체험 자동 시작 경로도 이 함수를 사용해야 최초 광고 UTM과 익명 방문자가
 * 신규 Firebase uid로 끊김 없이 연결된다.
 */
export const buildUserInitializationPayload = ({ eventId, metaMatchParams } = {}) => {
  const payload = {};

  try {
    const stored = localStorage.getItem('whatquiz_utm');
    if (stored) {
      const parsed = JSON.parse(stored);
      if (!parsed.expiresAt || Date.now() <= parsed.expiresAt) {
        payload.utm_source = parsed.utm_source || null;
        payload.utm_medium = parsed.utm_medium || null;
        payload.utm_campaign = parsed.utm_campaign || null;
        payload.utm_content = parsed.utm_content || null;
      }
    }
  } catch (_e) { /* ignore */ }

  try {
    payload.anonymous_id = getAnonymousUserId();
  } catch (_e) { /* ignore */ }

  try {
    const firstUtm = getFirstUtmFromStorage();
    if (
      firstUtm
      && (firstUtm.utm_source || firstUtm.utm_campaign || firstUtm.landing_page || firstUtm.utm_content)
    ) {
      payload.first_utm = {
        utm_source: firstUtm.utm_source || null,
        utm_medium: firstUtm.utm_medium || null,
        utm_campaign: firstUtm.utm_campaign || null,
        utm_content: firstUtm.utm_content || null,
        utm_term: firstUtm.utm_term || null,
        landing_page: firstUtm.landing_page || null,
        referrer: firstUtm.referrer || null,
      };
    }
  } catch (_e) { /* ignore */ }

  return {
    ...payload,
    ...(metaMatchParams || getMetaMatchParams()),
    event_id: eventId || createBrowserEventId(),
  };
};

/**
 * 서버가 사용자 문서를 처음 만들었다(is_new_user)는 응답을 받은 순간 가입 완료를 남긴다.
 * ensure-complete 를 부르는 곳이 여럿이라, 먼저 문서를 만든 쪽만 is_new_user 를 받는다.
 */
export const trackServerConfirmedSignup = (user) => {
  if (!user?.uid) return;
  const providerId = user.providerData?.[0]?.providerId || '';
  const method = providerId === 'google.com'
    ? 'google'
    : providerId === 'password' ? 'email' : providerId || 'unknown';
  trackSignupCompleteOnce(user.uid, method, 'server_init');
};

export const trackCompleteRegistrationMetaPixel = (payload = {}) => {
  if (typeof window === 'undefined' || typeof window.fbq === 'undefined') return;
  if (!payload.event_id) return;
  try {
    window.fbq('track', 'CompleteRegistration', {
      utm_source: payload.utm_source || undefined,
      utm_medium: payload.utm_medium || undefined,
      utm_campaign: payload.utm_campaign || undefined,
      utm_content: payload.utm_content || undefined,
    }, { eventID: payload.event_id });
  } catch (_e) { /* ignore */ }
};

/**
 * 토스 심사용 임시 사용자 API 헤더 생성
 * 
 * @param {Object} user - 사용자 객체 (Firebase 또는 임시 사용자)
 * @returns {Promise<Object>} - API 헤더 객체
 */
export const getAuthHeaders = async (user) => {
  const baseHeaders = getApiHeaders();
  
  // 임시 사용자 체크
  if (user && user.isTempReviewUser) {
    return {
      ...baseHeaders,
      'Authorization': `Bearer toss_temp_user_${user.uid}`
    };
  }
  
  // 일반 Firebase 사용자
  if (user && user.getIdToken) {
    try {
      // 🔧 토큰 강제 갱신 - 로그아웃/로그인 후 옛날 토큰 사용 방지
      const idToken = await user.getIdToken(true);  // force refresh!
      return {
        ...baseHeaders,
        'Authorization': `Bearer ${idToken}`
      };
    } catch (error) {
      console.error('Firebase 토큰 가져오기 실패:', error);
      return baseHeaders;
    }
  }
  
  return baseHeaders;
};

/**
 * 신규/기존 사용자 자동 초기화 함수
 * 백엔드의 /api/user/ensure-complete API 호출
 * 
 * @param {Object} user - Firebase Auth 사용자 객체
 * @returns {Promise<Object>} - 초기화 결과
 */
export const ensureUserInitialized = async (user) => {
  if (!user) {
    console.warn('❌ ensureUserInitialized: 사용자 정보가 없습니다.');
    return { success: false, error: 'No user provided' };
  }

  try {
  
    // 헤더 생성 (임시 사용자 지원)
    const headers = await getAuthHeaders(user);
    const initializationPayload = buildUserInitializationPayload();

    // API 호출
    const response = await fetch(getApiUrl('/api/user/ensure-complete'), {
      method: 'POST',
      headers: {
        ...headers,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(initializationPayload),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || '사용자 초기화 실패');
    }

    const result = await response.json();
    
    if (result.success) {
      const { is_new_user, schema_updated, user: userData, is_temp_review } = result;
      
      if (is_temp_review) {
        //console.log('🔍 토스 심사용 임시 사용자 초기화 완료:', userData.email);
      } else if (is_new_user) {
        // Google 가입 등 신규 사용자 → Meta Pixel CompleteRegistration (event_id로 CAPI와 중복 제거)
        trackCompleteRegistrationMetaPixel(initializationPayload);
        trackServerConfirmedSignup(user);
      } else {
        // 기존 사용자: 이전 화면에서 저장 확인 전에 끊긴 가입 완료가 있으면 이어 보낸다.
        resumePendingSignupComplete(user.uid);
      }
      
      return {
        success: true,
        isNewUser: is_new_user,
        schemaUpdated: schema_updated,
        userData: userData,
        isTempReview: is_temp_review,
        message: result.message
      };
    } else {
      throw new Error(result.error || '사용자 초기화 실패');
    }

  } catch (error) {
    console.error('❌ 사용자 초기화 실패:', error.message);
    
    // 사용자 초기화 실패는 로그인을 막지 않음 (fallback 처리)
    return {
      success: false,
      error: error.message,
      fallback: true // 로그인은 계속 진행
    };
  }
};

/**
 * 사용자 토큰 정보 새로고침
 * 
 * @param {Object} user - Firebase Auth 사용자 객체
 * @returns {Promise<Object>} - 토큰 정보
 */
export const refreshUserTokens = async (user) => {
  if (!user) return null;

  try {
    // 헤더 생성 (임시 사용자 지원)
    const headers = await getAuthHeaders(user);
    
    const response = await fetch(getApiUrl('/api/token/balance'), {
      method: 'GET',
      headers
    });

    if (response.ok) {
      const data = await response.json();
      return data;
    }
  } catch (error) {
    console.error('토큰 정보 새로고침 실패:', error);
  }
  
  return null;
};

/**
 * 재가입 제한 확인 (30일)
 * 
 * @param {string} email - 확인할 이메일 주소
 * @returns {Promise<Object>} - 제한 확인 결과
 */
export const checkRegistrationRestriction = async (email) => {
  if (!email) {
    return { 
      success: false, 
      error: 'Email is required' 
    };
  }

  try {
    const response = await fetch(getApiUrl(`/api/user/check-registration/${encodeURIComponent(email)}`), {
      method: 'GET',
      headers: getApiHeaders()
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || '재가입 제한 확인 실패');
    }

    const result = await response.json();
    
    if (result.success) {
      return {
        success: true,
        restriction: result.restriction
      };
    } else {
      throw new Error(result.error || '재가입 제한 확인 실패');
    }

  } catch (error) {
    console.error('❌ 재가입 제한 확인 실패:', error.message);
    
    // 에러 시 가입 허용 (안전한 기본값)
    return {
      success: false,
      error: error.message,
      restriction: {
        restricted: false,
        message: '회원가입이 가능합니다.'
      }
    };
  }
}; 